import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { translations } from '../translations';
import { getUserProfile, updateUserProfile, changePassword } from '../contexts/db.User';
import '../styles/pages/Profile.css';

function Profile({ language }) {
  const t = translations[language];
  const navigate = useNavigate();

  // Profile fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [userId, setUserId] = useState(null);

  // Password fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [profileMessage, setProfileMessage] = useState(null);
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [profileError, setProfileError] = useState(null);
  const [passwordError, setPasswordError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const user = await getUserProfile();
        setFirstName(user.first_name || '');
        setLastName(user.last_name || '');
        setEmail(user.email || '');
        setPhoneNumber(user.phone_number || '');
        setUserId(user.id);
      } catch (err) {
        console.error('Error loading profile:', err);
        if (err.response?.status === 401) {
          navigate('/');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [navigate]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileMessage(null);
    setProfileError(null);

    try {
      setSaving(true);
      const response = await updateUserProfile(firstName, lastName, phoneNumber);

      // Update localStorage so Navbar reflects changes immediately
      const updatedUser = response.user || { first_name: firstName, last_name: lastName, phone_number: phoneNumber };
      const stored = JSON.parse(localStorage.getItem('userData') || '{}');
      localStorage.setItem('userData', JSON.stringify({ ...stored, ...updatedUser }));

      setProfileMessage(t.profileUpdated || 'Profile updated successfully.');
    } catch (err) {
      console.error('Error saving profile:', err);
      setProfileError(err.response?.data?.message || 'Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage(null);
    setPasswordError(null);

    if (!currentPassword) {
      setPasswordError(t.currentPasswordRequired || 'Current password is required.');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordError(t.passwordMismatch || 'New passwords do not match.');
      return;
    }

    if (!newPassword || newPassword.length < 8) {
      setPasswordError(t.weakPassword || 'Password must be at least 8 characters.');
      return;
    }

    try {
      setChangingPassword(true);
      await changePassword(userId, currentPassword, newPassword);
      setPasswordMessage(t.passwordChanged || 'Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      console.error('Error changing password:', err);
      setPasswordError(err.response?.data?.message || 'Failed to change password.');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="profile-loading">
          <div className="loading-spinner"></div>
          <p>{t.loading || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-card">
        <div className="profile-header">
          <div className="profile-avatar">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
            </svg>
          </div>
          <h1>{t.myProfile || 'My Profile'}</h1>
        </div>

        {/* Personal Information Section */}
        <form className="profile-section" onSubmit={handleSaveProfile}>
          <h2>{t.personalInfo || 'Personal Information'}</h2>

          {profileMessage && <div className="profile-success">{profileMessage}</div>}
          {profileError && <div className="profile-error">{profileError}</div>}

          <div className="profile-form-grid">
            <div className="profile-field">
              <label htmlFor="firstName">{t.firstName || 'First Name'}</label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>

            <div className="profile-field">
              <label htmlFor="lastName">{t.lastName || 'Last Name'}</label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>

            <div className="profile-field">
              <label htmlFor="email">
                {t.email || 'Email'}
                <span className="field-readonly-badge">{t.emailReadOnly || 'Email cannot be changed.'}</span>
              </label>
              <input
                type="email"
                id="email"
                value={email}
                disabled
                className="input-readonly"
              />
            </div>

            <div className="profile-field">
              <label htmlFor="phoneNumber">{t.phoneNumber || 'Phone Number'}</label>
              <input
                type="tel"
                id="phoneNumber"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
            </div>
          </div>

          <div className="profile-actions">
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? '...' : (t.saveChanges || 'Save Changes')}
            </button>
          </div>
        </form>

        {/* Change Password Section */}
        <form className="profile-section" onSubmit={handleChangePassword}>
          <h2>{t.changePassword || 'Change Password'}</h2>

          {passwordMessage && <div className="profile-success">{passwordMessage}</div>}
          {passwordError && <div className="profile-error">{passwordError}</div>}

          <div className="profile-form-stack">
            <div className="profile-field">
              <label htmlFor="currentPassword">{t.currentPassword || 'Current Password'}</label>
              <input
                type="password"
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
            </div>

            <div className="profile-field">
              <label htmlFor="newPassword">{t.newPassword || 'New Password'}</label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
              />
            </div>

            <div className="profile-field">
              <label htmlFor="confirmNewPassword">{t.confirmNewPassword || 'Confirm New Password'}</label>
              <input
                type="password"
                id="confirmNewPassword"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                minLength={8}
              />
            </div>
          </div>

          <div className="profile-actions">
            <button type="submit" className="btn-password" disabled={changingPassword}>
              {changingPassword ? '...' : (t.updatePassword || 'Update Password')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Profile;
